/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Sagas for Labels Import
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.3.0
 */
import { createElement } from 'react';

import get from 'lodash/get';
import omit from 'lodash/omit';
import { SagaIterator } from 'redux-saga';
import { all, call, cancel, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import {
    loadTableFieldsSuccessful,
    loadTableListAction,
    loadTableListError,
    loadTableListSuccessful,
    setTableCurrentName,
    tableResetAction,
} from '../../components/Table/actions/tableActions';
import { TABLE_DENSITY_LOAD_SIZE, TABLE_LOAD_SIZE } from '../../components/Table/constants';
import { makeSelectorTableCurrentName, makeSelectorTableData } from '../../components/Table/selectors/tableSelectors';
import { TableNameType, TLoadTableListAction } from '../../components/Table/types/tableTypes';
import {
    API_HOSTNAME,
    LABELS_IMPORT_EXPORT_ERRORS,
    LABELS_IMPORT_UPLOAD,
    LABELS_IMPORT_VALIDATE,
    LABELS_IMPORT_VALIDATE_STATUS,
    LABELS_IMPORT_VALIDATED_LIST,
    WIZARD,
    WIZARD_ACTION,
} from '../../modules/app/Api';
import { EHttpMethod, HttpStatus } from '../../modules/app/appConstants';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '../../modules/internationalization/constants';
import {
    makeSelectLabelsImportTranslation,
    makeSelectTranslationCommon,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { ILabelsImportTranslation } from '../../modules/internationalization/types/wizards/labelsImport';
import { makeSelectIsDensity } from '../../modules/storage/storageSelectors';
import {
    wizardLoaderStartAction,
    wizardLoaderStopAction,
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStartAction,
    wizardStorageSaveStepAction,
} from '../../modules/wizard/wizardActions';
import {
    EWizardName,
    WIZARD_BACK_FAILED,
    WIZARD_CANCEL_ACTION,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_START_ACTION,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import {
    makeSelectWizardBasketSnapshotHeader,
    makeSelectWizardBasketSnapshotId,
    makeSelectWizardStepName,
} from '../../modules/wizard/wizardSelectors';
import {
    IWizardMetaStep,
    IWizardValidationError,
    TWizardBackFailedAction,
    TWizardMetaSuccessAction,
    TWizardNextFailedAction,
    TWizardNextSuccessAction,
    TWizardStartFailedAction,
} from '../../modules/wizard/wizardTypes';
import { checkResponseErrorWithModal, requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { checkIsFileExist } from '../../utils/checkIsFileExist';
import { stringFormat, stringFormatParams } from '../../utils/stringFormat';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectIsFeatureWithVersionEnabled,
} from '../PageAuth/selectors/authSelectors';
import {
    formLabelsImportTableFields,
    formLabelsImportTableList,
} from './components/LabelsImportTable/LabelsImportTableCells';
import {
    labelsImportClearWizardData,
    labelsImportHandleError,
    labelsImportLoadLabelsValidationFailure,
    labelsImportLoadLabelsValidationStart,
    labelsImportLoadLabelsValidationStop,
    labelsImportSetFileError,
    labelsImportSetFileMeta,
    labelsImportSetFileUploaded,
    labelsImportSetIsStepValid,
    labelsImportSetSavedParams,
    labelsImportSetValidationInfo,
} from './labelsImportActions';
import {
    ELabelsImportLabelsValidationRequest,
    ELabelsImportLabelsValidationStatus,
    ELabelsImportSteps,
    ELabelsImportValidationCodes,
    LABELS_IMPORT_CHECK_VALIDATION_DELAY_MS,
    LABELS_IMPORT_HANDLE_ERROR,
    LABELS_IMPORT_HANDLE_NEXT,
    LABELS_IMPORT_INIT_VALIDATED_LIST,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_START,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_STOP,
    LABELS_IMPORT_SET_FILE_ERROR,
    LABELS_IMPORT_SET_FILE_INFO_ITEMS,
    LABELS_IMPORT_SET_FILE_OBJECTS,
    LABELS_IMPORT_SET_FILE_UPLOADED,
    LABELS_IMPORT_SET_LABELS_VALIDATION_INFO,
    LABELS_IMPORT_SET_SAVED_PARAMS,
    LABELS_IMPORT_START_EXPORT_ERRORS,
    LABELS_IMPORT_TASK_PROCESSING_DELAY_MS,
    MapLabelsValidationRequestToHttpMethod,
} from './labelsImportConstants';
import {
    ILabelsImportFileInfoItem,
    ILabelsImportFileMeta,
    ILabelsImportLabelsValidationInfo,
    initialLabelsImportFileMetaState,
} from './labelsImportReducer';
import {
    makeSelectLabelsImportFileError,
    makeSelectLabelsImportFileInfoItems,
    makeSelectLabelsImportFileMeta,
    makeSelectLabelsImportFileObjects,
    makeSelectLabelsImportIsFileUploaded,
    makeSelectLabelsImportIsMaxRowsExceeded,
    makeSelectLabelsImportIsStepValid,
    makeSelectLabelsImportLabelsIsDataRestoredFromStorage,
    makeSelectLabelsImportLabelsValidationInfo,
    makeSelectLabelsImportTotalRowsCount,
} from './labelsImportSelectors';
import {
    ILabelsImportSetSavedParamsPayload,
    ILabelsImportUploadLabelsResponse,
    ILabelsImportValidatedSubscriberResponse,
    TLabelsImportHandleError,
    TLabelsImportLoadLabelsValidationStop,
} from './labelsImportTypes';

export function* labelsImportHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    let isUploadSuccess = true;
    if (stepName === ELabelsImportSteps.File) {
        const isFileUploaded: boolean = yield select(makeSelectLabelsImportIsFileUploaded());
        if (!isFileUploaded) {
            yield fork(labelsImportUploadFileSaga);
            const action = yield take([LABELS_IMPORT_SET_FILE_UPLOADED, LABELS_IMPORT_HANDLE_ERROR]);
            isUploadSuccess = action.type === LABELS_IMPORT_SET_FILE_UPLOADED;
        }
    }
    const isStepValid: boolean = yield select(makeSelectLabelsImportIsStepValid());
    if (isStepValid && isUploadSuccess) yield put(wizardNextAction({ options: { isSticky: true } }));
}

export function* labelsImportNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    if (stepName === ELabelsImportSteps.Final) {
        const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

        const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
            makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
        );

        if (isMegaPowersFeatureGreaterOrEqualThan2) {
            const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

            const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
            const goBack = () => breadCrumbGoBack(breadCrumbsData);

            yield put(
                modalOpen({
                    render: () =>
                        createElement(ModalMultipleOrders, {
                            isOpen: true,
                            orderOrOrders: orderId,
                            onClose: goBack,
                            onSuccessButtonClick: breadCrumbGoBack,
                            orderTypeTranlation: orderCreationModal.success.orderType.LABELS_IMPORT,
                        }),
                }),
            );
        } else {
            yield put(wizardOpenModalSummaryAction({ orderId }));
        }
    }
}

type TLabelsImportHandleErrorSagaActions =
    | TWizardNextFailedAction
    | TWizardBackFailedAction
    | TWizardStartFailedAction
    | TLabelsImportHandleError;

export function* labelsImportHandleErrorSaga(action: TLabelsImportHandleErrorSagaActions): SagaIterator {
    const errorCode = action.payload.response?.error?.code;
    const httpStatus = action.payload.code;
    const isWizardReset: true | undefined = action.payload.isWizardReset;

    const labelsImportTranslation: ILabelsImportTranslation = yield select(makeSelectLabelsImportTranslation());
    const fileMeta: ILabelsImportFileMeta = yield select(makeSelectLabelsImportFileMeta());
    const templateFormatsMessage = fileMeta.templateFormats.map((format) => format.toUpperCase()).join(', ');

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
    } else if (httpStatus === HttpStatus.NOT_FOUND && action.type === WIZARD_START_FAILED) {
        return;
    } else {
        const labelsImportErrorCode = labelsImportTranslation.error.codes[errorCode];
        const message = labelsImportErrorCode
            ? stringFormatParams(labelsImportErrorCode, { templateFormatsMessage })
            : labelsImportTranslation.error.defaultMessage;
        yield call(checkResponseErrorWithModal, action.payload, { message });
    }

    if (isWizardReset) yield put(wizardStartAction());
}

export function* labelsImportUploadFileSaga(): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const file: File = (yield select(makeSelectLabelsImportFileObjects()))[0];
        const isFileExist = yield call(checkIsFileExist, file);
        if (!isFileExist) {
            yield put(labelsImportSetFileError({ error: ELabelsImportValidationCodes.FILE_NOT_FOUND }));
            yield put(wizardLoaderStopAction());
            return;
        }

        const data = new FormData();
        data.append('file', file);

        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'accept': 'application/json;charset=UTF-8',
                'X-NODE': 'sticky',
                'X-BASKET-SNAPSHOT': header,
            },
        };

        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.LABELS_IMPORT) +
            stringFormat(WIZARD_ACTION, LABELS_IMPORT_UPLOAD);
        const response: ILabelsImportUploadLabelsResponse = yield call(
            requestSaga,
            url,
            EHttpMethod.POST,
            data,
            options,
        );
        yield put(
            labelsImportSetFileUploaded({
                isFileUploaded: true,
                isMaxRowsExceeded: response?.isMaxRowsExceeded ?? false,
                totalRowsCount: response?.insertedRowsCount ?? 0,
            }),
        );
        yield put(tableResetAction({ tableName: TableNameType.labelsImport }));
    } catch (error: any) {
        yield put(wizardLoaderStopAction());
        const validationError: IWizardValidationError[] | undefined = error.response.error?.validation;
        if (validationError && validationError[0]) {
            yield put(labelsImportSetFileError({ error: validationError[0].code }));
        } else {
            yield put(labelsImportHandleError(error));
        }
    }
}

export function* labelsImportStepInitSaga(action: TWizardMetaSuccessAction) {
    const isDataRestoredFromStorage: boolean = yield select(makeSelectLabelsImportLabelsIsDataRestoredFromStorage());
    if (!isDataRestoredFromStorage) yield call(labelsImportStorageRestoreSaga);

    const stepName: string = yield select(makeSelectWizardStepName());
    const wizardMetaStep: Omit<IWizardMetaStep, 'isFinal' | 'isFirst' | 'name'> = omit(action.payload.step, [
        'isFinal',
        'isFirst',
        'name',
    ]);

    switch (stepName) {
        case ELabelsImportSteps.File:
            yield put(labelsImportSetFileMeta({ ...wizardMetaStep.labelsImportSettings } as ILabelsImportFileMeta));
            break;
        case ELabelsImportSteps.Final:
            const labelsValidationInfo: ILabelsImportLabelsValidationInfo = { ...wizardMetaStep.uploadResult };
            yield put(labelsImportSetValidationInfo({ labelsValidationInfo }));
            yield put(setTableCurrentName(TableNameType.labelsImport));
            yield call(labelsImportFormTableFieldsSaga);
            switch (labelsValidationInfo.status) {
                case ELabelsImportLabelsValidationStatus.PREPARING:
                case ELabelsImportLabelsValidationStatus.RUNNING:
                case ELabelsImportLabelsValidationStatus.READY:
                    yield put(labelsImportLoadLabelsValidationStart());
                    break;
                case ELabelsImportLabelsValidationStatus.ERROR:
                    yield put(labelsImportHandleError({ isWizardReset: true }));
                    break;
            }
    }
    return;
}

export function* labelsImportWatchLabelsValidationSaga(): SagaIterator {
    const validationSaga = yield fork(labelsImportHandleLabelsValidationSaga);
    const action: TLabelsImportLoadLabelsValidationStop = yield take(LABELS_IMPORT_LOAD_LABELS_VALIDATION_STOP);
    yield cancel(validationSaga);
    if (action.payload.isInterrupt) {
        yield call(labelsImportInterruptSaga);
    }
}

export function* labelsImportInterruptSaga(): SagaIterator {
    yield put(wizardLoaderStartAction());

    yield call(labelsImportLoadLabelsValidationSaga, ELabelsImportLabelsValidationRequest.INTERRUPT);
    let labelsValidationInfo: ILabelsImportLabelsValidationInfo = yield call(
        labelsImportLoadLabelsValidationSaga,
        ELabelsImportLabelsValidationRequest.CHECK,
    );
    while (labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.RUNNING) {
        yield delay(LABELS_IMPORT_TASK_PROCESSING_DELAY_MS);
        labelsValidationInfo = yield call(
            labelsImportLoadLabelsValidationSaga,
            ELabelsImportLabelsValidationRequest.CHECK,
        );
    }

    yield put(wizardLoaderStopAction());
}

export function* labelsImportHandleLabelsValidationSaga(): SagaIterator {
    let labelsValidationInfo: ILabelsImportLabelsValidationInfo = yield select(
        makeSelectLabelsImportLabelsValidationInfo(),
    );

    yield put(wizardLoaderStartAction());

    // Wait task labels validation will be ready to start
    while (labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.PREPARING) {
        yield delay(LABELS_IMPORT_TASK_PROCESSING_DELAY_MS);
        labelsValidationInfo = yield call(
            labelsImportLoadLabelsValidationSaga,
            ELabelsImportLabelsValidationRequest.CHECK,
        );
    }

    // Start task for labels validation
    if (labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.READY) {
        yield call(labelsImportLoadLabelsValidationSaga, ELabelsImportLabelsValidationRequest.START);
    }

    yield put(wizardLoaderStopAction());

    // Check validation status until it completed or interrupted
    while (
        labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.READY ||
        labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.RUNNING
    ) {
        labelsValidationInfo = yield call(
            labelsImportLoadLabelsValidationSaga,
            ELabelsImportLabelsValidationRequest.CHECK,
        );
        yield delay(LABELS_IMPORT_CHECK_VALIDATION_DELAY_MS);
    }
}

export function* labelsImportLoadLabelsValidationSaga(
    labelsValidationRequestType: ELabelsImportLabelsValidationRequest,
): SagaIterator {
    const httpMethod = MapLabelsValidationRequestToHttpMethod[labelsValidationRequestType];
    try {
        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'accept': 'application/json;charset=UTF-8',
                'X-NODE': 'sticky',
                'X-BASKET-SNAPSHOT': header,
            },
        };

        const apiUrl =
            labelsValidationRequestType === ELabelsImportLabelsValidationRequest.CHECK
                ? LABELS_IMPORT_VALIDATE_STATUS
                : LABELS_IMPORT_VALIDATE;
        const url =
            API_HOSTNAME + stringFormat(WIZARD, EWizardName.LABELS_IMPORT) + stringFormat(WIZARD_ACTION, apiUrl);
        const labelsValidationInfo: ILabelsImportLabelsValidationInfo | undefined = yield call(
            requestSaga,
            url,
            httpMethod,
            null,
            options,
        );

        if (labelsValidationInfo?.status === ELabelsImportLabelsValidationStatus.ERROR) {
            yield put(labelsImportHandleError({ isWizardReset: true }));
            yield put(labelsImportLoadLabelsValidationStop({}));
        } else if (labelsValidationRequestType === ELabelsImportLabelsValidationRequest.CHECK && labelsValidationInfo) {
            yield put(labelsImportSetValidationInfo({ labelsValidationInfo }));
        }
        return labelsValidationInfo;
    } catch (e: any) {
        yield put(labelsImportLoadLabelsValidationFailure({ errorMessage: e.toString() }));
        yield put(wizardLoaderStopAction());
        yield put(labelsImportHandleError({ ...e, isWizardReset: true }));
        yield put(labelsImportLoadLabelsValidationStop({}));
    }
}

export function* labelsImportInitValidatedListSaga(): SagaIterator {
    yield put(setTableCurrentName(TableNameType.labelsImport));
    yield call(labelsImportFormTableFieldsSaga);
    yield put(loadTableListAction({ force: true }));
}

export function* labelsImportStepValidateSaga(): SagaIterator {
    const stepName: string = yield select(makeSelectWizardStepName());
    let isStepValid = true;
    let validateError = '';

    switch (stepName) {
        case ELabelsImportSteps.File:
            const fileError: string = yield select(makeSelectLabelsImportFileError());
            const fileInfoItems: ILabelsImportFileInfoItem[] = yield select(makeSelectLabelsImportFileInfoItems());
            if (fileError || !fileInfoItems.length) {
                isStepValid = false;
            }
            break;
        case ELabelsImportSteps.Final:
            // служебный пользователь может дойти до конца визрда, но завершить не может
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }
            const labelsValidationInfo: ILabelsImportLabelsValidationInfo = yield select(
                makeSelectLabelsImportLabelsValidationInfo(),
            );
            if (labelsValidationInfo.successRowsCount === 0) {
                isStepValid = false;
            }
            break;
    }
    yield put(labelsImportSetIsStepValid({ isStepValid, validateError }));
    return;
}

export function* labelsImportStorageSaveSaga(): SagaIterator {
    const fileInfoItems: ILabelsImportFileInfoItem[] = yield select(makeSelectLabelsImportFileInfoItems());
    const fileMeta: ILabelsImportFileMeta = yield select(makeSelectLabelsImportFileMeta());
    const isFileUploaded: boolean = yield select(makeSelectLabelsImportIsFileUploaded());
    const isMaxRowsExceeded: boolean = yield select(makeSelectLabelsImportIsMaxRowsExceeded());
    const totalRowsCount: number = yield select(makeSelectLabelsImportTotalRowsCount());

    const params: ILabelsImportSetSavedParamsPayload = {
        fileInfoItems,
        isFileUploaded,
        isMaxRowsExceeded,
        totalRowsCount,
        [ELabelsImportSteps.File]: fileMeta,
    };

    yield put(wizardStorageSaveStepAction({ params }));
}

export function* labelsImportStorageRestoreSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());

    const localStorageParams: ILabelsImportSetSavedParamsPayload = get(localStorageState, `${basketSnapshotId}`, {
        fileInfoItems: [],
        isFileUploaded: false,
        isMaxRowsExceeded: false,
        totalRowsCount: 0,
        [ELabelsImportSteps.File]: initialLabelsImportFileMetaState,
    });

    yield put(labelsImportSetSavedParams(localStorageParams));
}

export function* labelsImportClearWizardDataSaga(): SagaIterator {
    yield put(labelsImportClearWizardData());
}

export function* labelsImportExportErrorsSaga(): SagaIterator {
    const url =
        API_HOSTNAME +
        stringFormat(WIZARD, EWizardName.LABELS_IMPORT) +
        stringFormat(WIZARD_ACTION, LABELS_IMPORT_EXPORT_ERRORS);
    window.open(url, '_blank');
}

export function* labelsImportFormTableFieldsSaga(): SagaIterator {
    const labelsValidationInfo: ILabelsImportLabelsValidationInfo = yield select(
        makeSelectLabelsImportLabelsValidationInfo(),
    );
    const { maxLabelsCount } = labelsValidationInfo;
    if (!maxLabelsCount) return;

    let labelsImportTranslation: ILabelsImportTranslation = yield select(makeSelectLabelsImportTranslation());
    if (labelsImportTranslation.title === '') {
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        labelsImportTranslation = yield select(makeSelectLabelsImportTranslation());
    }

    const labelsImportFields = formLabelsImportTableFields(maxLabelsCount, labelsImportTranslation.table);
    yield put(loadTableFieldsSuccessful({ fields: labelsImportFields }));
}

export function* labelsImportLoadValidatedListSaga(action: TLoadTableListAction): SagaIterator {
    const start = action.payload?.force ?? false;
    const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
    const isDensity: boolean = yield select(makeSelectIsDensity());
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const list = yield select(makeSelectorTableData());
    const tableLoadSize = isDensity ? TABLE_DENSITY_LOAD_SIZE : TABLE_LOAD_SIZE;
    const labelsValidationInfo: ILabelsImportLabelsValidationInfo = yield select(
        makeSelectLabelsImportLabelsValidationInfo(),
    );

    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-NODE': 'sticky',
            'X-BASKET-SNAPSHOT': header,
        },
    };

    try {
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.LABELS_IMPORT) +
            stringFormat(WIZARD_ACTION, LABELS_IMPORT_VALIDATED_LIST);
        const queryString = formQueryParamsString({
            from: start ? 0 : list.length,
            size: tableLoadSize,
        });
        const tableData: { results: ILabelsImportValidatedSubscriberResponse[] } = yield call(
            requestSaga,
            url + queryString,
            EHttpMethod.GET,
            null,
            options,
        );

        let labelsImportTranslation: ILabelsImportTranslation = yield select(makeSelectLabelsImportTranslation());
        if (labelsImportTranslation.title === '') {
            yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
            labelsImportTranslation = yield select(makeSelectLabelsImportTranslation());
        }
        const formattedData = formLabelsImportTableList(tableData.results, labelsImportTranslation);

        yield put(
            loadTableListSuccessful({
                tableName: tableName,
                data: [...(start ? [] : list), ...formattedData],
                count: labelsValidationInfo.subscriberCount,
                basketAllIn: false,
                tableFooter: undefined,
            }),
        );
    } catch (e: any) {
        yield put(loadTableListError({ tableName: tableName, error: e.toString() }));
        yield put(labelsImportHandleError(e));
    }
}

export function* labelsImportSagas(): SagaIterator {
    yield all([
        takeEvery(LABELS_IMPORT_HANDLE_NEXT, labelsImportHandleNextSaga),
        takeEvery(WIZARD_META_SUCCESS, labelsImportStepInitSaga),
        takeEvery(
            [
                LABELS_IMPORT_LOAD_LABELS_VALIDATION_STOP,
                LABELS_IMPORT_SET_FILE_ERROR,
                LABELS_IMPORT_SET_FILE_OBJECTS,
                LABELS_IMPORT_SET_FILE_INFO_ITEMS,
                LABELS_IMPORT_SET_LABELS_VALIDATION_INFO,
                LABELS_IMPORT_SET_SAVED_PARAMS,
                WIZARD_META_SUCCESS,
                WIZARD_NEXT_FAILED,
            ],
            labelsImportStepValidateSaga,
        ),
        takeEvery([WIZARD_START_ACTION, WIZARD_CANCEL_ACTION], labelsImportClearWizardDataSaga),
        takeEvery([WIZARD_START_SUCCESS, LABELS_IMPORT_SET_FILE_UPLOADED], labelsImportStorageSaveSaga),
        takeEvery(LABELS_IMPORT_START_EXPORT_ERRORS, labelsImportExportErrorsSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, labelsImportNextSuccessSaga),
        takeEvery(
            [LABELS_IMPORT_HANDLE_ERROR, WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_START_FAILED],
            labelsImportHandleErrorSaga,
        ),
        takeEvery(LABELS_IMPORT_LOAD_LABELS_VALIDATION_START, labelsImportWatchLabelsValidationSaga),
        takeEvery(LABELS_IMPORT_INIT_VALIDATED_LIST, labelsImportInitValidatedListSaga),
    ]);
}
